import { createEffect, createResource, createSignal, For, Show } from "solid-js";
import { A, useNavigate, useParams } from "@solidjs/router";
import { useApi, isOk } from "../api";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import { showConfirm } from "../components/Dialog";
import Text from "../components/Text";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import ItemStackView from "../components/ItemStackView";
import CurrencyInput from "../components/CurrencyInput";
import AhListingName from "../components/AhListingName";
import ItemIdentityEditor, { type ItemIdentityRuleApi } from "../components/ItemIdentityEditor";
import ItemIdentityWizard, { type WizardItemSource } from "../components/ItemIdentityWizard";
import { formatInteger } from "../services/numberFormat";
import { formatDateTime } from "../services/dateFormat";
import { ahListingWizardSource, listingLore, toStack } from "../services/ahListing";
import type { AhWatchDto, AhWatchRequest, AhListingDto, SellbotWizardRequest } from "../api/generated/schemas";
import styles from "./AhWatches.module.css";

const LEAD_CHOICES = [5, 10, 15, 30, 60, 120, 240];

function toRequest(watch: AhWatchDto): AhWatchRequest {
  return {
    name: watch.name,
    isEnabled: watch.isEnabled,
    itemId: watch.itemId,
    metadata: watch.metadata,
    snbt: watch.snbt ?? null,
    maxPrice: watch.maxPrice ?? null,
    binOnly: watch.binOnly,
    notifyOnNewMatch: watch.notifyOnNewMatch,
    notifyBeforeExpiry: watch.notifyBeforeExpiry,
    notifyOnOutbid: watch.notifyOnOutbid,
    notifyOnPriceBelowCeiling: watch.notifyOnPriceBelowCeiling,
    leadMinutes: [...(watch.leadMinutes ?? [])],
    rules: [],
  };
}

export default function AhWatch() {
  const api = useApi();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  const [saving, setSaving] = createSignal(false);
  const [priceDraft, setPriceDraft] = createSignal<number | "">("");
  const [wizardOpen, setWizardOpen] = createSignal(false);

  const [watch, { refetch }] = createResource(
    () => params.watchId,
    async (id) => {
      const res = await api.ahWatch.get(id);
      return isOk(res.data) ? res.data.value! : null;
    }
  );

  const [matches] = createResource(
    () => [params.watchId, watch()?.rules.length ?? 0] as const,
    async ([id]) => {
      const res = await api.ahWatch.matches(id);
      return isOk(res.data) ? res.data.value!.items : ([] as AhListingDto[]);
    }
  );

  createEffect(() => setPriceDraft(watch()?.maxPrice ?? ""));

  // The settings card saves as a whole; rules save one dimension at a time through the editor.
  const save = async (patch: Partial<AhWatchRequest>) => {
    const current = watch();
    if (!current) return;

    setSaving(true);
    try {
      const res = await api.ahWatch.update(current.id, { ...toRequest(current), ...patch });
      if (!isOk(res.data)) {
        toast?.error(t("AH_WATCH_SAVE_FAILED"));
        return;
      }
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const current = watch();
    if (!current) return;
    if (!await showConfirm(t("AH_WATCH_DELETE_CONFIRM", current.name), true)) return;

    const res = await api.ahWatch.delete(current.id);
    if (!isOk(res.data)) {
      toast?.error(t("AH_WATCH_DELETE_FAILED"));
      return;
    }
    toast?.success(t("AH_WATCH_DELETED"));
    navigate("/ah/watches");
  };

  const ruleApi = (): ItemIdentityRuleApi => ({
    create: async (rule) => {
      const res = await api.ahWatch.createRule(params.watchId, rule);
      return { ok: isOk(res.data) };
    },
    update: async (ruleId, rule) => {
      const res = await api.ahWatch.updateRule(params.watchId, ruleId, rule);
      return { ok: isOk(res.data) };
    },
    remove: async (ruleId) => {
      const res = await api.ahWatch.deleteRule(params.watchId, ruleId);
      return { ok: isOk(res.data) };
    },
    reload: async () => { await refetch(); },
    preview: async (request) => {
      const res = await api.ahWatch.rulePreview(request);
      return isOk(res.data) ? res.data.value! : null;
    },
  });

  const wizardSource = ahListingWizardSource(api, "AH_WATCH_PICK_ITEM");

  const wizardDiff = async (request: SellbotWizardRequest) => {
    const res = await api.ahWatch.wizardDiff(request);
    return isOk(res.data)
      ? { ok: true as const, value: res.data.value! }
      : { ok: false as const, error: t("AH_WATCH_SAVE_FAILED") };
  };

  const wizardFinish = async (request: SellbotWizardRequest) => {
    const res = await api.ahWatch.applyRules(params.watchId, request);
    if (!isOk(res.data)) return { ok: false, error: t("AH_WATCH_SAVE_FAILED") };
    await refetch();
    return { ok: true };
  };

  const toggleLead = (minutes: number) => {
    const leads = watch()?.leadMinutes ?? [];
    void save({
      leadMinutes: leads.includes(minutes)
        ? leads.filter((existing: number) => existing !== minutes)
        : [...leads, minutes].sort((left, right) => right - left),
    });
  };

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <A href="/ah/watches" class="btn btn-secondary">
          <i class="fa-solid fa-arrow-left" /> <Text val="AH_WATCH_BACK" />
        </A>
      </div>

      <Show
        when={watch.state !== "pending"}
        fallback={<div class={styles.grid}><div class={styles.skeletonCard} /></div>}
      >
        <Show
          when={watch()}
          fallback={
            <EmptyState illustration="jobs" title={t("AH_WATCH_NOT_FOUND")} hint={t("AH_WATCH_NOT_FOUND_HINT")} />
          }
        >
          {(current) => (
            <>
              <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
                <div class={styles.detailHeader}>
                  <ItemStackView
                    stack={{ itemId: current().itemId, count: 1, damage: current().metadata, snbt: current().snbt ?? undefined }}
                  />
                  <div class="v" style={{ gap: "2px", flex: 1 }}>
                    <span class={styles.detailName}>{current().name}</span>
                    <span class={styles.subtitle}>
                      <Text val="AH_WATCH_MATCHES" />: {formatInteger(current().activeMatchCount)}
                      <Show when={current().nextExpiryUtc}>
                        {" · "}<Text val="AH_WATCH_NEXT_EXPIRY" />: {formatDateTime(current().nextExpiryUtc!)}
                      </Show>
                    </span>
                  </div>
                  <Badge tone={current().isEnabled ? "success" : "neutral"} pill>
                    <Text val={current().isEnabled ? "AH_WATCH_ENABLED" : "AH_WATCH_DISABLED"} />
                  </Badge>
                  <button class="btn btn-danger btn-sm" onClick={remove}>
                    <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                  </button>
                </div>

                <div class={styles.fieldRow}>
                  <label class={styles.field}>
                    <span><Text val="AH_WATCH_NAME" /></span>
                    <input
                      type="text"
                      value={current().name}
                      maxLength={80}
                      disabled={saving()}
                      onChange={(e) => void save({ name: e.currentTarget.value })}
                    />
                  </label>
                  <label class={styles.field}>
                    <span><Text val="AH_WATCH_MAX_PRICE" /></span>
                    {/* CurrencyInput reports every keystroke, so the write waits for the field to be left. */}
                    <div onFocusOut={() => {
                      const next = priceDraft() === "" ? null : Number(priceDraft());
                      if (next !== (current().maxPrice ?? null)) void save({ maxPrice: next });
                    }}>
                      <CurrencyInput value={priceDraft()} onChange={setPriceDraft} aria-label={t("AH_WATCH_MAX_PRICE")} />
                    </div>
                  </label>
                  <label class={styles.checkField}>
                    <input
                      type="checkbox"
                      checked={current().binOnly}
                      disabled={saving()}
                      onChange={(e) => void save({ binOnly: e.currentTarget.checked })}
                    />
                    <Text val="AH_FILTER_BIN_ONLY" />
                  </label>
                  <label class={styles.checkField}>
                    <input
                      type="checkbox"
                      checked={current().isEnabled}
                      disabled={saving()}
                      onChange={(e) => void save({ isEnabled: e.currentTarget.checked })}
                    />
                    <Text val="AH_WATCH_ENABLED" />
                  </label>
                </div>
              </div>

              <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
                <div class={styles.sectionHeader}>
                  <h2><Text val="AH_WATCH_NOTIFY" /></h2>
                  <span class={styles.subtitle}><Text val="AH_WATCH_NOTIFY_HINT" /></span>
                </div>
                <div class={styles.fieldRow}>
                  <label class={styles.checkField}>
                    <input type="checkbox" checked={current().notifyOnNewMatch} disabled={saving()}
                      onChange={(e) => void save({ notifyOnNewMatch: e.currentTarget.checked })} />
                    <Text val="AH_WATCH_NOTIFY_NEW" />
                  </label>
                  <label class={styles.checkField}>
                    <input type="checkbox" checked={current().notifyBeforeExpiry} disabled={saving()}
                      onChange={(e) => void save({ notifyBeforeExpiry: e.currentTarget.checked })} />
                    <Text val="AH_WATCH_NOTIFY_EXPIRY" />
                  </label>
                  <label class={styles.checkField}>
                    <input type="checkbox" checked={current().notifyOnOutbid} disabled={saving()}
                      onChange={(e) => void save({ notifyOnOutbid: e.currentTarget.checked })} />
                    <Text val="AH_WATCH_NOTIFY_OUTBID" />
                  </label>
                  <label class={styles.checkField}>
                    <input type="checkbox" checked={current().notifyOnPriceBelowCeiling}
                      disabled={saving() || current().maxPrice == null}
                      onChange={(e) => void save({ notifyOnPriceBelowCeiling: e.currentTarget.checked })} />
                    <Text val="AH_WATCH_NOTIFY_CEILING" />
                    <Show when={current().maxPrice == null}>
                      <span class={styles.checkHint}><Text val="AH_WATCH_NOTIFY_CEILING_NEEDS_PRICE" /></span>
                    </Show>
                  </label>
                </div>

                <Show when={current().notifyBeforeExpiry}>
                  <div class="v" style={{ gap: "var(--spacing-xs)" }}>
                    <span class={styles.groupLabel}><Text val="AH_WATCH_LEADS" /></span>
                    <div class={styles.leadRow}>
                      <For each={LEAD_CHOICES}>
                        {(minutes) => (
                          <button
                            type="button"
                            disabled={saving()}
                            class={`${styles.leadChip} ${(current().leadMinutes ?? []).includes(minutes) ? styles.leadChipOn : ""}`}
                            onClick={() => toggleLead(minutes)}
                          >
                            {minutes}m
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
              </div>

              <ItemIdentityEditor
                itemId={current().itemId}
                metadata={current().metadata}
                snbt={current().snbt}
                rules={current().rules}
                dimensions={current().dimensions}
                ruleApi={ruleApi()}
                headerAction={<button type="button" class="btn btn-sm btn-secondary" onClick={() => setWizardOpen(true)}><i class="fa-solid fa-wand-magic-sparkles" /> <Text val="AH_WATCH_WIZARD_START" /></button>}
              />

              <ItemIdentityWizard open={wizardOpen()} onClose={() => setWizardOpen(false)}
                source={wizardSource} diff={wizardDiff} onFinish={wizardFinish} />

              <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
                <div class={styles.sectionHeader}>
                  <h2><Text val="AH_WATCH_CURRENT_MATCHES" /></h2>
                </div>
                <Show
                  when={(matches()?.length ?? 0) > 0}
                  fallback={
                    <p class={styles.subtitle}>
                      <i class="fa-solid fa-magnifying-glass" /> <Text val="AH_WATCH_NO_MATCHES" />
                    </p>
                  }
                >
                  <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                    <For each={matches()!}>
                      {(listing) => (
                        <A href={`/ah?listing=${listing.id}`} class={styles.matchRow}>
                          <ItemStackView stack={toStack(listing)} size={32} loreOverride={listingLore(listing.snbt)} />
                          <span class={styles.matchName}>
                            <AhListingName listing={listing} />
                          </span>
                          <span class={styles.matchBid}>{formatInteger(listing.currentBid ?? 0)}$</span>
                          <span class={styles.matchExpiry}>{formatDateTime(listing.estimatedExpiryUtc!)}</span>
                        </A>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
